import { ShieldCheck } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PageHeader } from '@/components/layout/page-header'
import { StaffUserManagement } from '@/app/(dashboard)/settings/users/_components/StaffUserManagement'
import { apiClient } from '@/lib/api/client'
import { getHelmSessionUser } from '@/src/lib/auth'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'

export function UsersPage() {
  const { data, error, loading, reload } = useAsyncData(async () => {
    const [user, profile] = await Promise.all([
      getHelmSessionUser(),
      apiClient.getProfile().catch(() => null),
    ])
    const isAdmin = !!profile?.is_admin || profile?.role === 'admin'
    const staff = isAdmin ? await apiClient.listHelmUsers().catch(() => null) : null

    return { user, profile, staff, isAdmin }
  }, [])

  if (loading) return <LoadingState label="Loading users..." />
  if (error) return <ErrorState error={error} onRetry={reload} />

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Users"
        description="Manage Helm staff access and role invitations"
        breadcrumbs={[
          { label: 'Settings', href: '/settings' },
          { label: 'Users' },
        ]}
      />

      <div className="flex-1 overflow-auto p-4 md:p-6">
        {!data?.isAdmin ? (
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription>
              Admin access is required to manage Helm users.
            </AlertDescription>
          </Alert>
        ) : data.staff && data.user ? (
          <StaffUserManagement
            users={data.staff.users}
            invitations={data.staff.invitations}
            currentUserId={data.user.id}
          />
        ) : (
          <Alert variant="destructive">
            <AlertDescription>
              Unable to load Helm users from the API.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  )
}
